import { isAxiosError } from 'axios';

import { SC_SITES_BACKEND } from '@/config/env';
import { inventoryV3Api } from '@/lib/inventoryV3Api';
import { siteComplianceApi } from '@/lib/siteComplianceApi';

import { normalizeSiteComplianceDivision } from '../siteCompliance.selectors';
import type { SiteComplianceSite } from '../siteCompliance.types';
import {
  mapResourceDtoToSiteComplianceInventoryResource,
  mapSiteConfigurationDto,
  mapSiteDetailDtoToFacts,
  mapSiteDtoToSiteComplianceSite,
} from './mappers';
import type {
  ResourcesPageDto,
  SiteConfigurationDto,
  SiteDetailDto,
  SiteListFilters,
  SitesBackendMode,
  SitesPageDto,
  SitesQueryMeta,
  SitesSource,
} from './types';
import {
  mapInventoryResourceToUi,
  mapInventorySiteDetailToUi,
  mapInventorySiteToUi,
  mapNetboxDatasetResourceToUi,
} from './inventory/mappers';
import {
  buildInventoryResourcesOData,
  buildInventorySitesOData,
  buildNetboxSiteResourcesOData,
} from './inventory/odata';
import type {
  InventoryDatasetPageDto,
  InventoryResourceDto,
  InventoryResourcesPageDto,
  InventorySiteDetailDto,
  InventorySiteDto,
  NetboxDatasetResourceDto,
} from './inventory/types';

const DEFAULT_PAGE_SIZE = 100;
const NETBOX_SITE_RESOURCE_DATASETS = [
  {
    name: 'NetboxDcimDevices',
    select: 'id,name,siteId,details',
  },
  {
    name: 'NetboxDcimRacks',
    select: 'id,name,siteId,details',
  },
  {
    name: 'NetboxIpamPrefixes',
    select: 'id,prefix,siteId,details',
  },
  {
    name: 'NetboxIpamVlan',
    select: 'id,name,siteId,details',
  },
];

const normalizeSearchText = (value: unknown) =>
  `${value ?? ''}`.trim().toLowerCase();

const normalizeDivisionFilter = (value: unknown) =>
  normalizeSiteComplianceDivision(`${value ?? ''}`);

const matchesTextFilter = (value: unknown, filter: string | undefined) => {
  const normalizedFilter = normalizeSearchText(filter);

  if (!normalizedFilter) {
    return true;
  }

  return normalizeSearchText(value).includes(normalizedFilter);
};

const matchesBooleanFilter = (
  value: boolean,
  filter: boolean | undefined,
) => filter === undefined || value === filter;

const matchesSiteFilters = (
  site: SiteComplianceSite,
  filters: SiteListFilters = {},
) => {
  const normalizedSearch = normalizeSearchText(filters.search);
  const searchableText = [
    site.siteId,
    site.siteName,
    site.workdayId,
    site.region,
    site.country,
    site.primaryDivision,
    ...site.primaryDivisions,
    ...site.businessUnits,
  ]
    .join(' ')
    .toLowerCase();

  const matchesSearch =
    !normalizedSearch || searchableText.includes(normalizedSearch);
  const matchesPrimaryDivision =
    !filters.primaryDivision ||
    site.primaryDivisions.some(
      (division) =>
        normalizeDivisionFilter(division) ===
        normalizeDivisionFilter(filters.primaryDivision),
    ) ||
    normalizeDivisionFilter(site.primaryDivision) ===
      normalizeDivisionFilter(filters.primaryDivision);
  const matchesBusinessUnit =
    !filters.businessUnit ||
    site.businessUnits.some(
      (businessUnit) =>
        normalizeSearchText(businessUnit) ===
        normalizeSearchText(filters.businessUnit),
    );

  return (
    matchesSearch &&
    matchesTextFilter(site.siteId, filters.siteId) &&
    matchesTextFilter(site.siteName, filters.siteName) &&
    matchesTextFilter(site.workdayId, filters.workdayId) &&
    matchesTextFilter(site.region, filters.region) &&
    matchesPrimaryDivision &&
    matchesBusinessUnit &&
    (!filters.siteType || site.siteType === filters.siteType) &&
    (filters.networkTier === undefined ||
      filters.networkTier === null ||
      site.networkTier === filters.networkTier) &&
    matchesBooleanFilter(site.isFlagship, filters.flagship) &&
    matchesBooleanFilter(site.isEnhanced, filters.enhanced) &&
    matchesBooleanFilter(site.isDatacenter, filters.datacenter)
  );
};

const paginate = <T>(
  items: T[],
  filters: SiteListFilters = {},
): {
  continuationToken: string | null;
  items: T[];
} => {
  const pageSize = Math.max(1, filters.pageSize ?? DEFAULT_PAGE_SIZE);
  const startIndex = Number(filters.cursor ?? 0);
  const resolvedStartIndex = Number.isFinite(startIndex) ? startIndex : 0;
  const pageItems = items.slice(resolvedStartIndex, resolvedStartIndex + pageSize);
  const nextIndex = resolvedStartIndex + pageItems.length;

  return {
    continuationToken: nextIndex < items.length ? `${nextIndex}` : null,
    items: pageItems,
  };
};

const withMeta = <T extends object>(
  data: T,
  meta: SitesQueryMeta,
): T & SitesQueryMeta => ({
  ...data,
  ...meta,
});

const isInventoryFallbackError = (error: unknown) => {
  if (!isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;

  return status === undefined || status === 404 || status >= 500;
};

const extractArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;

    if (Array.isArray(record.items)) {
      return record.items;
    }

    if (Array.isArray(record.data)) {
      return record.data;
    }

    if (Array.isArray(record.resources)) {
      return record.resources;
    }

    if (Array.isArray(record.values)) {
      return record.values;
    }

    if ('body' in record) {
      return extractArray(record.body);
    }
  }

  return [];
};

const toInventorySiteDto = (value: unknown): InventorySiteDto => {
  if (value && typeof value === 'object') {
    return value as InventorySiteDto;
  }

  return {};
};

const toInventoryResourceDto = (value: unknown): InventoryResourceDto => {
  if (value && typeof value === 'object') {
    return value as InventoryResourceDto;
  }

  return {};
};

const toNetboxDatasetResourceDto = (value: unknown): NetboxDatasetResourceDto => {
  if (value && typeof value === 'object') {
    return value as NetboxDatasetResourceDto;
  }

  return {};
};

const shouldUseInventorySitesDataset = (filters: SiteListFilters = {}) =>
  Boolean(
    filters.businessUnit ||
      filters.datacenter !== undefined ||
      filters.enhanced !== undefined ||
      filters.flagship !== undefined ||
      filters.networkTier ||
      filters.primaryDivision ||
      filters.region ||
      filters.siteType,
  );

const fetchInventorySitesViaDataset = async (
  filters: SiteListFilters = {},
) => {
  const response = await inventoryV3Api.post<
    InventoryDatasetPageDto<InventorySiteDto>
  >(
    '/api/v3/inventory/datasets/netbox/NetboxDcimSites',
    buildInventorySitesOData(filters),
  );

  return extractArray(response.data).map(toInventorySiteDto);
};

const fetchInventorySitesViaList = async () => {
  const response = await inventoryV3Api.get<InventorySiteDto[]>(
    '/api/v3/inventory/sites',
  );

  return extractArray(response.data).map(toInventorySiteDto);
};

const fetchInventorySites = async (filters: SiteListFilters = {}) => {
  if (shouldUseInventorySitesDataset(filters)) {
    try {
      return await fetchInventorySitesViaDataset(filters);
    } catch (error) {
      if (isInventoryFallbackError(error)) {
        throw error;
      }
    }
  }

  return fetchInventorySitesViaList();
};

const fetchInventorySiteDetail = async (siteId: string) => {
  const response = await inventoryV3Api.get<InventorySiteDetailDto>(
    `/api/v3/inventory/sites/${encodeURIComponent(siteId)}`,
  );

  return response.data;
};

const getInventorySiteNumericId = (site: InventorySiteDetailDto | null) => {
  if (!site?.id) {
    return null;
  }

  const numericId = Number(site.id);
  return Number.isFinite(numericId) ? numericId : null;
};

const fetchInventoryCloudResources = async (siteId: string) => {
  const response = await inventoryV3Api.post<InventoryResourcesPageDto>(
    '/api/v3/inventory/resources',
    buildInventoryResourcesOData({
      siteId,
    }),
  );

  return extractArray(response.data)
    .map(toInventoryResourceDto)
    .map((resource) => mapInventoryResourceToUi(resource, siteId));
};

const fetchNetboxSiteResources = async (
  siteId: string,
  siteNumericId: number,
) => {
  const datasetResults = await Promise.all(
    NETBOX_SITE_RESOURCE_DATASETS.map(async (dataset) => {
      const response = await inventoryV3Api.post<
        InventoryDatasetPageDto<NetboxDatasetResourceDto>
      >(
        `/api/v3/inventory/datasets/netbox/${dataset.name}`,
        buildNetboxSiteResourcesOData({
          select: dataset.select,
          siteNumericId,
        }),
      );

      return extractArray(response.data)
        .map(toNetboxDatasetResourceDto)
        .map((resource) =>
          mapNetboxDatasetResourceToUi(resource, siteId, dataset.name),
        );
    }),
  );

  return datasetResults.flat();
};

const apiSource: SitesSource = {
  getSiteConfiguration: async (siteId: string) => {
    const response = await siteComplianceApi.get<SiteConfigurationDto>(
      `/api/v1/site-compliance/sites/${encodeURIComponent(
        siteId,
      )}/configuration`,
    );
    const configuration = mapSiteConfigurationDto(response.data, siteId);

    return withMeta(
      {
        configurations: [configuration],
      },
      {
        backendUnavailable: false,
        source: 'api',
      },
    );
  },
  getSiteDetail: async (siteId: string) => {
    const response = await siteComplianceApi.get<SiteDetailDto>(
      `/api/v1/site-compliance/sites/${encodeURIComponent(siteId)}`,
    );
    const facts = mapSiteDetailDtoToFacts(response.data);

    return withMeta(
      {
        ...facts,
        latestConfiguration: null,
      },
      {
        backendUnavailable: false,
        source: 'api',
      },
    );
  },
  getSiteResources: async (siteId: string) => {
    const response = await siteComplianceApi.get<ResourcesPageDto>(
      `/api/v1/site-compliance/sites/${encodeURIComponent(siteId)}/resources`,
    );

    return withMeta(
      {
        items: (response.data?.items ?? []).map((resource) =>
          mapResourceDtoToSiteComplianceInventoryResource(resource, siteId),
        ),
      },
      {
        backendUnavailable: false,
        source: 'api',
      },
    );
  },
  kind: 'api',
  listSites: async (filters: SiteListFilters = {}) => {
    const response = await siteComplianceApi.get<SitesPageDto>(
      '/api/v1/site-compliance/sites',
      {
        params: {
          businessUnit: filters.businessUnit || undefined,
          cursor: filters.cursor || undefined,
          datacenter: filters.datacenter,
          enhanced: filters.enhanced,
          flagship: filters.flagship,
          networkTier: filters.networkTier || undefined,
          pageSize: filters.pageSize,
          primaryDivision: filters.primaryDivision || undefined,
          region: filters.region || undefined,
          search: filters.search || undefined,
          siteId: filters.siteId || undefined,
          siteName: filters.siteName || undefined,
          siteType: filters.siteType || undefined,
          workdayId: filters.workdayId || undefined,
        },
      },
    );

    return withMeta(
      {
        continuationToken: response.data?.continuationToken ?? null,
        items: (response.data?.items ?? []).map(mapSiteDtoToSiteComplianceSite),
      },
      {
        backendUnavailable: false,
        source: 'api',
      },
    );
  },
};

const inventorySource: SitesSource = {
  getSiteConfiguration: async () =>
    withMeta(
      {
        configurations: [],
        unsupportedReason: "inventory backend doesn't support configuration",
      },
      {
        backendUnavailable: false,
        source: 'inventory',
      },
    ),
  getSiteDetail: async (siteId: string) => {
    const detail = await fetchInventorySiteDetail(siteId);
    const facts = mapInventorySiteDetailToUi(detail);

    return withMeta(
      facts,
      {
        backendUnavailable: false,
        source: 'inventory',
      },
    );
  },
  getSiteResources: async (siteId: string) => {
    const detail = await fetchInventorySiteDetail(siteId);
    const siteNumericId = getInventorySiteNumericId(detail);
    const cloudResources = await fetchInventoryCloudResources(siteId);
    const netboxResources = siteNumericId
      ? await fetchNetboxSiteResources(siteId, siteNumericId)
      : [];

    return withMeta(
      {
        items: [...cloudResources, ...netboxResources],
      },
      {
        backendUnavailable: false,
        source: 'inventory',
      },
    );
  },
  kind: 'inventory',
  listSites: async (filters: SiteListFilters = {}) => {
    const filteredSites = (await fetchInventorySites(filters))
      .map(mapInventorySiteToUi)
      .filter((site) => matchesSiteFilters(site, filters))
      .sort((left, right) => left.siteId.localeCompare(right.siteId));
    const page = paginate(filteredSites, filters);

    return withMeta(
      {
        continuationToken: page.continuationToken,
        items: page.items,
      },
      {
        backendUnavailable: false,
        source: 'inventory',
      },
    );
  },
};

const resolveSitesBackendMode = (): SitesBackendMode => SC_SITES_BACKEND;

export const getSitesSource = () => {
  const mode = resolveSitesBackendMode();

  if (mode === 'api') {
    return apiSource;
  }

  if (mode === 'inventory') {
    return inventorySource;
  }

  return apiSource;
};

export const getSitesBackendMode = resolveSitesBackendMode;
